import {useFlux} from '@nlabs/arkhamjs-utils-react';
import {Gotham} from '@nlabs/gothamui';
import {Metropolis, resolveEnvironmentConfig} from '@nlabs/metropolisjs';
import {Suspense, useEffect, useMemo, useState} from 'react';

import {SessionActivityRefresh} from './SessionActivityRefresh.js';
import {SessionLoading} from './SessionLoading.js';
import {ThemeProvider} from './theme.js';
import {getWorkspaceAnalyticsId} from './workspaceAnalytics.js';
import {createEnvironment} from './workspaceEnvironment.js';

import type {ReactElement} from 'react';
import type {WorkspaceProviderProps} from './WorkspaceProvider.js';

export const WorkspaceRuntime = ({appId, children, name, routes}: WorkspaceProviderProps): ReactElement => {
  const flux = useFlux();
  const [analytics, setAnalytics] = useState<{appId: string; analyticsId?: string}>();
  const [configReady, setConfigReady] = useState(false);
  const analyticsId = analytics?.appId === appId ? analytics.analyticsId : undefined;

  useEffect(() => {
    let mounted = true;
    const prepareRuntime = async () => {
      const configuredAnalyticsId = import.meta.env.PROD ? await getWorkspaceAnalyticsId(appId) : undefined;
      if(mounted) {
        setAnalytics({analyticsId: configuredAnalyticsId, appId});
      }
    };
    void prepareRuntime();
    return () => {
      mounted = false;
    };
  }, [appId]);

  const config = useMemo(() => {
    const environment = createEnvironment(appId, name, import.meta.env.PROD, analyticsId);
    return {development: environment, local: environment, production: environment};
  }, [analyticsId, appId, name]);

  useEffect(() => {
    let mounted = true;
    const prepareMetropolis = async () => {
      await Promise.resolve(flux.setState('app.config', resolveEnvironmentConfig(config)));
      if(mounted) {
        setConfigReady(true);
      }
    };
    void prepareMetropolis();
    return () => {
      mounted = false;
    };
  }, [config, flux]);

  const gothamConfig = useMemo(() => ({
    app: {name, title: name},
    authRoute: '/sign-in',
    displayMode: 'light' as const,
    isAuth: () => Boolean(flux.getState('user.session.userId', '')),
    routes: routes || [
      {analytics: {route: '/', title: name, viewId: `${appId}.workspace`}, element: children, index: true},
      {analytics: {route: '/*', title: name, viewId: `${appId}.workspace`}, element: children, path: '*'}
    ],
    storageType: false as const
  }), [appId, children, flux, name, routes]);

  return configReady
    ? <Metropolis config={config}>
      <ThemeProvider>
        <SessionActivityRefresh />
        <Suspense fallback={<SessionLoading />}>
          <Gotham config={gothamConfig} />
        </Suspense>
      </ThemeProvider>
    </Metropolis>
    : <SessionLoading />;
};
